import { z } from 'zod';
import { normalizeHungarianPhone } from './phone.js';
import { normalizeAndValidateIban } from './iban.js';
import { isPinFormatValid, isPinWeak } from './pin.js';

export const EmailSchema = z.string().trim().toLowerCase().email().max(254);
export const DisplayNameSchema = z
  .string()
  .trim()
  .min(2)
  .max(32)
  .regex(/^[A-Za-z0-9_.\- ]+$/, 'Only letters, digits, and _ . - space allowed');
export const FullNameSchema = z.string().trim().min(2).max(120);
export const PhoneSchema = z.string().transform((v, ctx) => {
  try {
    return normalizeHungarianPhone(v);
  } catch (e) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: (e as Error).message,
    });
    return z.NEVER;
  }
});
export const IbanSchema = z.string().transform((v, ctx) => {
  try {
    return normalizeAndValidateIban(v);
  } catch (e) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: (e as Error).message });
    return z.NEVER;
  }
});
export const PinSchema = z
  .string()
  .refine(isPinFormatValid, 'PIN must be 4 digits')
  .refine((v) => !isPinWeak(v), 'PIN is too common — pick something less obvious');

export const RegisterSchema = z.object({
  email: EmailSchema,
  displayName: DisplayNameSchema,
  fullName: FullNameSchema,
  iban: IbanSchema,
  phone: PhoneSchema,
  pin: PinSchema,
});
export type RegisterInput = z.input<typeof RegisterSchema>;
export type RegisterParsed = z.output<typeof RegisterSchema>;

export const LoginSchema = z.object({
  email: EmailSchema,
  pin: z.string().refine(isPinFormatValid, '4 digits required'),
});
export type LoginInput = z.input<typeof LoginSchema>;

// Price entered in EUR (e.g. "3.50"); stored in cents after parsing.
export const PriceStringSchema = z
  .string()
  .regex(/^\d+([.,]\d{1,2})?$/, 'Use a price like 3.50');

export const ProductInputSchema = z.object({
  name: z.string().trim().min(1).max(200),
  brand: z.string().trim().max(120).nullish(),
  imageUrl: z.string().trim().url().max(1000).nullish(),
});
export type ProductInput = z.input<typeof ProductInputSchema>;

export const BarcodeSchema = z
  .string()
  .trim()
  .regex(/^(\d{8,14}|csoki-[0-9A-HJKMNP-TV-Z]{26})$/i, 'Not a valid barcode or internal id');

export const ListingUpsertSchema = z.object({
  productBarcode: BarcodeSchema,
  priceCents: z.number().int().min(0).max(99_999_999_99),
  stock: z.number().int().min(0).max(10_000),
});
export type ListingUpsertInput = z.infer<typeof ListingUpsertSchema>;

export const ListingPatchSchema = z.object({
  priceCents: z.number().int().min(0).max(99_999_999_99).optional(),
  stock: z.number().int().min(0).max(10_000).optional(),
});

export const PurchaseCreateSchema = z.object({
  listingId: z.string().min(10).max(40),
  quantity: z.number().int().min(1).max(100),
});
export type PurchaseCreateInput = z.infer<typeof PurchaseCreateSchema>;
